import { useTranslation } from 'react-i18next';
import { icons } from '@koob/ui';
import { useApp } from 'hooks';

// The DMC's reference-data areas, from this one list so the settings modal's
// sections (settingsSections) and the page header's tabs (useSettingsArea)
// never drift. A page shows when its permission allows it. Each area is a
// section of the modal, its pages as the header's tabs.
const AREAS = {
  locations: {
    path: '/locations',
    label: 'locations',
    icon: icons.locations,
    pages: [
      { to: '/locations/countries', label: 'countries', can: 'readLocations' },
      { to: '/locations/cities', label: 'cities', can: 'readLocations' },
      { to: '/locations/markets', label: 'markets', can: 'readLocations' },
    ],
  },
  hotels: {
    path: '/settings/hotels',
    label: 'catalog.hotels',
    icon: icons.hotels,
    pages: [
      ['categories', 'categories'],
      ['styles', 'styles'],
      ['locations', 'locations'],
      ['accreditations', 'accreditations'],
      ['general-categories', 'descriptions'],
      ['dmc-categories', 'dmcCategories'],
      ['room-types', 'roomTypes'],
      ['bed-types', 'bedTypes'],
      ['sustainable-clusters', 'sustainableClusters'],
      ['facilities-groups/facilities', 'facilities'],
      ['facilities-groups/groups', 'facilityGroups'],
      ['meal-plans', 'mealPlans'],
    ].map(([slug, label]) => ({
      to: `/settings/hotels/${slug}`,
      label,
      can: 'readHotelSettings',
    })),
  },
  experiences: {
    path: '/settings/experiences',
    label: 'catalog.experiences',
    icon: icons.experiences,
    pages: [
      {
        to: '/settings/experiences/segments',
        label: 'segments',
        can: 'readExperienceSettings',
      },
      {
        to: '/settings/experiences/themes',
        label: 'themes',
        can: 'readExperienceSettings',
      },
    ],
  },
  travelers: {
    path: '/settings/travelers',
    label: 'catalog.travelers',
    icon: icons.travelers,
    pages: [
      {
        to: '/settings/travelers/traveler-kinds',
        label: 'categories',
        can: 'readTravelerSettings',
      },
    ],
  },
  travelDiary: {
    path: '/settings/travel-diary',
    label: 'travelDiarySettings',
    icon: icons.travelDiary,
    pages: [
      {
        to: '/settings/travel-diary/sections',
        label: 'travelDiarySection',
        can: 'readTravelDiarySettings',
      },
      {
        to: '/settings/travel-diary/content-library',
        label: 'travelDiaryContentLibrary',
        can: 'readTravelDiaryContent',
      },
    ],
  },
};

const allowedPages = (area, t, isAllowedTo) =>
  AREAS[area].pages
    .filter((page) => isAllowedTo(page.can))
    .map((page) => ({ to: page.to, label: t(page.label) }));

// The modal's link to an area: its first allowed page, active on all of them;
// null when the user may see none.
const areaLink = (area, t, isAllowedTo) => {
  const pages = allowedPages(area, t, isAllowedTo);
  return pages.length
    ? {
        to: pages[0].to,
        icon: AREAS[area].icon,
        label: t(AREAS[area].label),
        activeMatch: [AREAS[area].path],
      }
    : null;
};

/** Paths the settings modal shows (App.js renders them over the last page). */
const SETTINGS_PATHS = [
  '/settings',
  '/locations',
  '/user/notification-preferences',
];

export const isSettingsPath = (pathname) =>
  SETTINGS_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

/** The settings modal's sections: the user's own, then the organization's
 * (empty groups left out). */
export const settingsSections = (t, isAllowedTo) =>
  [
    {
      label: t('settingsModal.user'),
      links: [
        {
          to: '/user/notification-preferences',
          icon: icons.notification,
          label: t('settingsModal.notifications'),
          activeMatch: ['/user/notification-preferences'],
        },
      ],
    },
    {
      label: t('settingsModal.organization'),
      links: [
        isAllowedTo('readAuditLog') && {
          to: '/settings/audit-log',
          icon: icons.history,
          label: t('auditLog'),
          activeMatch: ['/settings/audit-log'],
        },
        areaLink('hotels', t, isAllowedTo),
        areaLink('experiences', t, isAllowedTo),
        areaLink('travelers', t, isAllowedTo),
        areaLink('travelDiary', t, isAllowedTo),
        areaLink('locations', t, isAllowedTo),
      ].filter(Boolean),
    },
  ].filter((group) => group.links.length);

/** For a page of an area: the header's title and tabs. */
export function useSettingsArea(area) {
  const { t } = useTranslation('mainSidebar');
  const { isAllowedTo } = useApp();
  const tabs = allowedPages(area, t, isAllowedTo);
  // One page: its title says it all, no tab row.
  return { title: t(AREAS[area].label), tabs: tabs.length > 1 ? tabs : [] };
}
